import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import TextField from '@material-ui/core/TextField';
import AddCircle from '@material-ui/icons/AddCircle';
import AddIcon from '@material-ui/icons/Add';
import WarningIcon from '@material-ui/icons/Warning';
import InputIcon from '@material-ui/icons/Input';
import PropertiesIcon from '@material-ui/icons/List';
import ParametersIcon from '@material-ui/icons/Settings';
import iconWithAction from 'grb-webui/components/IconWithAction';
import AppMainPage, { FIT_TO_PAGE_VARIANT } from '../../components/AppMainPage';
import DropZone from '../../components/DropZone';
import StorageObjectListItem from '../batches/StorageObjectListItem';
import { FileListItem } from '../../components/FileListItem';
import { RepositoryIcon } from '../repository';
import {
  MODEL_EXTENSIONS,
  INITIALIZATION_EXTENSIONS,
  PARAMETER_EXTENSIONS,
  COMPRESSION_EXTENSIONS,
  ALL_INPUTFILE_EXTENSIONS,
} from '../../services/BatchFileValidator';
import SelectStorageObjectDialog from './SelectStorageObjectDialog';
import SelectResultFileTypeDialog from './SelectResultFileTypeDialog';
import ResultFileTypeListitem from './ResultFileListItem';
import ParameterItem from './ParameterItem';
import SubmitCard from './SubmitCard';

import './SubmitBatchContainer.scss';

const AddFromRepositoryIcon = iconWithAction({
  BackgroundIcon: RepositoryIcon,
  ForegroundIcon: AddCircle,
  left: -6,
  bottom: -6,
  background: '#f50057',
  style: { marginTop: '-8px' },
});

const OutputIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path d="M23,12L19,8V11H10V13H19V16M1,18V6C1,4.89 1.9,4 3,4H15A2,2 0 0,1 17,6V9H15V6H3V18H15V15H17V18A2,2 0 0,1 15,20H3A2,2 0 0,1 1,18Z" />
  </svg>
);

export default () => {
  class SubmitBatchContainer extends React.PureComponent {
    constructor(props) {
      super(props);

      // Bindings
      this.handleReset = this.handleReset.bind(this);
      this.handleAbort = this.handleAbort.bind(this);
      this.handleSolve = this.handleSolve.bind(this);
      this.filterRepoObject = this.filterRepoObject.bind(this);
      this.handleUploadNodesChanged = this.handleUploadNodesChanged.bind(this);
      this.handleUploadFilesAdded = this.handleUploadFilesAdded.bind(this);
      this.handleRemoveUploadFile = this.handleRemoveUploadFile.bind(this);

      this.handleOpenRepoDialog = this.handleOpenRepoDialog.bind(this);
      this.handleCloseRepoDialog = this.handleCloseRepoDialog.bind(this);
      this.handleRemoveRepoObject = this.handleRemoveRepoObject.bind(this);
      this.filterRepositoryObject = this.filterRepositoryObject.bind(this);
      this.handleOpenResultDialog = this.handleOpenResultDialog.bind(this);
      this.handleCloseResultDialog = this.handleCloseResultDialog.bind(this);
      this.getSelectedTypes = this.getSelectedTypes.bind(this);
      this.handleCompressFileType = this.handleCompressFileType.bind(this);
      this.handleUnuseResultTypeFile = this.handleUnuseResultTypeFile.bind(this);
      this.handleChangePriority = this.handleChangePriority.bind(this);
      this.handleChangeApplication = this.handleChangeApplication.bind(this);
      this.handleChangeGroup = this.handleChangeGroup.bind(this);
      this.handleCreateParameter = this.handleCreateParameter.bind(this);
      this.handleChangeParamName = this.handleChangeParamName.bind(this);
      this.handleChangeParamValue = this.handleChangeParamValue.bind(this);
      this.handleDeleteParam = this.handleDeleteParam.bind(this);

      //
      // Get uploader
      //
      const { batchService } = props;
      this.uploader = batchService.getBatchCreateUploader();
      const modelFileExtensions = MODEL_EXTENSIONS.concat(INITIALIZATION_EXTENSIONS, PARAMETER_EXTENSIONS);
      this.invalidExtensionMessage = `Invalid file extension(s).\nAccepted files are ${modelFileExtensions.concat(COMPRESSION_EXTENSIONS).join(', ')}`;
      this.inputFileExtensions = ALL_INPUTFILE_EXTENSIONS;

      this.uploadSubscriber = {
        filesChanged: (uploadNodes, ctx) => {
          this.updateStateWithValidation({ uploadNodes }, ctx);
        },
        uploadStarted: () => {
          this.setState({
            uploading: true,
            uploadNodes: this.uploader.getFileNodes(), // Potential errors are cleared, need to update
            repoNodes: this.uploader.getRepositoryNodes(),
          });
        },
        uploadEnded: () => {
          this.setState({ uploading: false });
        },
      };
      this.state = {
        ...this.getStateFromUploader(),
        selectObjectDialogOpen: false,
        selectResultDialogOpen: false,
      };
    }

    componentDidMount() {
      this.uploader.subscribeUpload(this.uploadSubscriber);
      this.loadObjects();
    }

    componentWillUnmount() {
      this.uploader.unsubscribeUpload(this.uploadSubscriber);
    }

    getStateFromUploader() {
      return {
        uploading: this.uploader.isUploading(),
        uploadNodes: this.uploader.getFileNodes(),
        priority: this.uploader.getPriority(),
        app: this.uploader.getApplication(),
        group: this.uploader.getGroup(),
        repoNodes: this.uploader.getRepositoryNodes(),
        resultFiles: this.uploader.getResultFiles(),
        parameters: this.uploader.getParameters(),
      };
    }

    loadObjects() {
      const { repositoryService } = this.props;
      repositoryService.fetchAllObjects().then((objects) => {
        this.allObjects = objects.filter(o => this.filterRepoObject(o));
      });
    }

    handleReset() {
      this.uploader.reset().then(() => {
        this.setState(this.getStateFromUploader());
      });
    }

    handleAbort() {
      this.uploader.abort();
    }

    handleSolve() {
      this.serverError = null;
      this.uploader.upload()
        .then((batchId) => {
          this.setState(this.getStateFromUploader(), () => {
            const { history } = this.props;
            history.push({ pathname: '/batch/list', state: { selectedBatchId: batchId } });
          });
        })
        .catch((err) => {
          const { viewService } = this.props;
          viewService.onError(err);
          try {
            this.serverError = (err.response && err.err.response.data && err.response.data.error && err.response.data.error || err).message;
          } catch (e) {
            console.log('Exception processing error', err);
            console.log(e);
          }
          this.setState(this.getStateFromUploader());
        });
    }

    updateStateWithValidation(newState, ctx /* validation context */) {
      const state = newState && { ...newState } || {};
      let changed = false;
      if (ctx.validationChanged) {
        if (!newState || !newState.uploadNodes) {
          state.uploadNodes = this.uploader.getFileNodes().slice();
        }
        changed = true;
      }
      if (ctx.repoNodesChanged) {
        state.repoNodes = this.uploader.getRepositoryNodes();
        changed = true;
      }
      if (ctx.resultFilesChanged) {
        state.resultFiles = this.uploader.getResultFiles();
        changed = true;
      }
      if (changed || newState) {
        this.setState(state);
      }
    }

    validateAfterChange(newState) {
      const ctx = {};
      this.uploader.validateFiles(ctx);
      this.updateStateWithValidation(newState, ctx);
    }

    //
    // Handle repository objects
    //

    filterRepositoryObject(object) {
      return !this.state.repoNodes.find(node => (node.object === object) || (node.object.id === object.id));
    }

    handleOpenRepoDialog() {
      this.setState({ selectObjectDialogOpen: true });
    }

    filterRepoObject(object) {
      const index = object.name.lastIndexOf('.');
      const extension = (index >= 0) && object.name.substring(index + 1);
      return !extension || (extension !== 'log');
    }

    handleCloseRepoDialog(object) {
      if (object) {
        this.validateAfterChange({
          selectObjectDialogOpen: false,
          repoNodes: this.uploader.addRepositoryObject(object),
        });
      } else {
        this.setState({ selectObjectDialogOpen: false });
      }
    }

    handleRemoveRepoObject(object) {
      this.validateAfterChange({ repoNodes: this.uploader.removeRepositoryObject(object) });
    }

    handleChangePriority(event) {
      this.uploader.setPriority(parseInt(event.target.value, 10));
      this.setState({ priority: this.uploader.getPriority() });
    }

    handleChangeGroup(event) {
      this.uploader.setGroup(event.target.value);
      this.setState({ group: this.uploader.getGroup() });
    }

    handleChangeApplication(event) {
      this.uploader.setApplication(event.target.value);
      this.setState({ app: this.uploader.getApplication() });
    }

    //
    // Manage file results
    //

    handleOpenResultDialog() {
      this.setState({ selectResultDialogOpen: true });
    }

    handleCloseResultDialog(types) {
      if (types) {
        const resultFiles = this.uploader.getResultFiles();
        this.validateAfterChange({
          selectResultDialogOpen: false,
          resultFiles: this.uploader.setResultFiles(types.map(type => (resultFiles.find(f => f.type === type) || { type, compr: 'none' }))),
        });
      } else {
        this.setState({ selectResultDialogOpen: false });
      }
    }

    getSelectedTypes() {
      return this.uploader.getResultFiles().map(file => file.type);
    }

    handleChangeResultFile(file, prop, value) {
      this.validateAfterChange({
        resultFiles: this.uploader.changeResultFile(file, prop, value),
      });
    }

    handleCompressFileType(file, compr) {
      this.handleChangeResultFile(file, 'compr', compr);
    }

    handleUnuseResultTypeFile(file) {
      this.validateAfterChange({
        resultFiles: this.uploader.removeResultFile(file),
      });
    }

    //
    // Manage input files
    //

    handleUploadNodesChanged(uploadNodes) {
      this.setState({ uploadNodes });
    }

    handleUploadFilesAdded(files) {
      this.uploader.addFiles(files);
    }

    handleRemoveUploadFile(node) {
      this.uploader.removeFiles([node]);
    }

    validateInput() {
      const { uploadNodes, repoNodes } = this.state;
      if ((uploadNodes.length === 0) && (repoNodes.length === 0)) {
        return 'Please, add input files';
      }
      if (uploadNodes.find(n => n.validationError) || repoNodes.find(n => n.validationError)) {
        return 'Please, fix input file issues';
      }
      return null;
    }

    validateResult() {
      const { resultFiles } = this.state;
      if (resultFiles.find(node => node.validationError)) {
        return 'Please, fix result file issues';
      }
      return null;
    }

    //
    // Manage parameters
    //
    handleChangeParamName(index, name) {
      this.setState({ parameters: this.uploader.changeParameterName(index, name) });
    }

    handleChangeParamValue(index, value) {
      this.setState({ parameters: this.uploader.changeParameterValue(index, value) });
    }

    handleDeleteParam(index) {
      this.setState({ parameters: this.uploader.deleteParameter(index) });
    }

    handleCreateParameter() {
      this.setState({ parameters: this.uploader.addParameter('', '') });
    }

    validateParameters() {
      const { parameters } = this.state;
      if (parameters.find(param => param.nameError || param.valueError)) {
        return 'Please, fix parameters issues';
      }
      return null;
    }

    getWarning() {
      const { resultFiles } = this.state;
      if (!resultFiles.length) {
        return 'No result files specified';
      }
      return null;
    }

    render() {
      const { repositoryService, configuration } = this.props;
      const {
        uploading,
        priority,
        group,
        app,
        uploadNodes,
        repoNodes,
        selectObjectDialogOpen,
        resultFiles,
        selectResultDialogOpen,
        parameters,
      } = this.state;
      const validationError = this.validateInput() || this.validateResult() || this.validateParameters();
      const warning = !validationError && this.getWarning();
      const getParamKey = (param, index) => {
        return `${index}`; // Only immutable possible key while editing a parameter, as both name and value can be edited.
      };

      //
      // Header
      //
      const HeaderBar = (
        <div className="grb-main-page-header-buttons">
          <Button
            variant="contained"
            color="primary"
            aria-label="reset"
            onClick={uploading ? this.handleAbort : this.handleReset}
            id="abort-or-reset-submit"
          >
            {uploading ? 'Abort' : 'Reset'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            aria-label="upload"
            onClick={this.handleSolve}
            id="solve-or-upload-button"
            disabled={!!validationError || uploading}
          >
            {uploading ? 'Uploading' : 'Solve' }
          </Button>
        </div>
      );

      return (
        <AppMainPage
          route={this.props.route}
          variant={FIT_TO_PAGE_VARIANT}
          TrailingBar={HeaderBar}
          className="grb-submit-batch-page"
          help={configuration && configuration.help}
        >
          <div
            className="grb-createbatch-component"
          >
            <header className={classnames('grb-createbatch-header', validationError && 'grb-validation-error', warning && 'grb-warning')}>
              <Typography className={classnames('grb-title', validationError && 'grb-validation-error', warning && 'grb-warning')}>
                {(validationError || warning) && <WarningIcon />}
                {validationError || warning || ''}
                {this.serverError && (
                  <div style={{ display: 'none' }} id="createBatchServerError">{this.serverError}</div>
                )}
              </Typography>
            </header>
            <div className="grb-createbatch-content">
              {/* Input files */}
              <SubmitCard
                id="submit-inputfiles-card"
                icon={InputIcon}
                title="Input files"
                buttonIcon={AddFromRepositoryIcon}
                buttonTooltip="Add object from Repository"
                onButtonClick={this.handleOpenRepoDialog}
                buttonAriaLabel="Add object from repository"
              >
                <DropZone
                  onFilesAdded={this.handleUploadFilesAdded}
                  validExtensions={this.inputFileExtensions}
                  invalidExtensionMessage={this.invalidExtensionMessage}
                />
                <List
                  className="grb-createbatch-objectslist"
                  component="nav"
                  aria-label="list of objects"
                  dense
                  disablePadding
                >
                  {uploadNodes.map(node => (
                    <FileListItem
                      key={node.name}
                      node={node}
                      onDelete={this.handleRemoveUploadFile}
                      uploader={this.uploader}
                      repositoryService={repositoryService}
                    />
                  ))}
                  {repoNodes.map(node => (
                    <StorageObjectListItem
                      key={node.object.id}
                      object={node.object}
                      canDelete
                      onDelete={this.handleRemoveRepoObject}
                      repositoryService={repositoryService}
                      AvatarComponent={RepositoryIcon}
                      error={node.validationError}
                    />
                  ))}
                  {((uploadNodes.length === 0) && (repoNodes.length === 0)) && (
                    <li key="NoInputFile" className="grb-emptylist-item">No input files</li>
                  )}
                </List>
              </SubmitCard>
              {/* Result files */}
              <SubmitCard
                id="submit-results-card"
                icon={OutputIcon}
                title="Result files"
                buttonIcon={AddIcon}
                buttonTooltip="Add result file types"
                onButtonClick={this.handleOpenResultDialog}
                buttonAriaLabel="Add result file"
              >
                <List className="grb-resultlist" disablePadding>
                  {resultFiles.map(file => (
                    <ResultFileTypeListitem
                      file={file}
                      key={file.type.ext}
                      error={file.validationError}
                      hideDescription
                      onRemove={this.handleUnuseResultTypeFile}
                      onCompressFileType={this.handleCompressFileType}
                    />
                  ))}
                  {(resultFiles.length === 0) && (
                    <li key="NoResultFile" className="grb-emptylist-item">No result files</li>
                  )}
                </List>
              </SubmitCard>
              {/* Properties */}
              <SubmitCard
                id="submit-properties-card"
                icon={PropertiesIcon}
                title="Properties"
              >
                <form>
                  <TextField
                    placeholder="Enter a priority"
                    label="Priority"
                    value={priority}
                    type="number"
                    InputProps={{ inputProps: { min: 0 } }}
                    onChange={this.handleChangePriority}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    placeholder="Enter a group"
                    label="Group"
                    value={group}
                    onChange={this.handleChangeGroup}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    placeholder="Enter an application"
                    label="Application"
                    value={app}
                    onChange={this.handleChangeApplication}
                    fullWidth
                    margin="normal"
                  />
                </form>
              </SubmitCard>
              {/* Parameters */}
              <SubmitCard
                id="submit-parameters-card"
                icon={ParametersIcon}
                title="Parameters"
                buttonIcon={AddIcon}
                buttonTooltip="Add parameter"
                onButtonClick={this.handleCreateParameter}
                buttonAriaLabel="Add parameter"
              >
                <List className="grb-paramlist" disablePadding>
                  {parameters.map((param, i) => (
                    <ParameterItem
                      key={getParamKey(param, i)}
                      index={i}
                      name={param.name}
                      nameError={param.nameError}
                      onChangeName={this.handleChangeParamName}
                      value={param.value}
                      valueError={param.valueError}
                      onChangeValue={this.handleChangeParamValue}
                      onDelete={this.handleDeleteParam}
                    />
                  ))}
                </List>
              </SubmitCard>
            </div>
          </div>
          {selectObjectDialogOpen && (
            <SelectStorageObjectDialog
              {...this.props}
              open={selectObjectDialogOpen}
              onClose={this.handleCloseRepoDialog}
              filterStorageObject={this.filterRepositoryObject}
            />
          )}
          {selectResultDialogOpen && (
            <SelectResultFileTypeDialog
              {...this.props}
              open={selectResultDialogOpen}
              onClose={this.handleCloseResultDialog}
              selectedTypes={this.getSelectedTypes()}
            />
          )}
        </AppMainPage>
      );
    }
  }

  SubmitBatchContainer.propTypes = {
    configuration: PropTypes.shape({
      help: PropTypes.shape({}),
    }),
    viewService: PropTypes.shape({
      onError: PropTypes.func.isRequired,
    }).isRequired,
    batchService: PropTypes.shape({
      getBatchCreateUploader: PropTypes.func.isRequired,
    }).isRequired,
    repositoryService: PropTypes.shape({
      fetchAllObjects: PropTypes.func,
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    route: PropTypes.shape({}).isRequired,
  };

  SubmitBatchContainer.defaultProps = {
    configuration: undefined,
  };

  return SubmitBatchContainer;
};

